import { z } from "zod/v4";

import type { OfferDto, OfferLikeObject } from "../../dtos/offers";
import { WidgetConfigSchema, WidgetTypeList } from "../../dtos/widgets";
import { RecursivePartial } from "../../utils";

export const WidgetDeviceTypeList = ["mobile", "desktop"] as const;
export type WidgetDeviceType = (typeof WidgetDeviceTypeList)[number];

export interface WidgetConfigType<
  TVariant extends string,
  TStyles extends object,
  TVariables extends object,
> {
  desktop?: WidgetDeviceConfigType<TVariant, TStyles, TVariables>;
  mobile?: WidgetDeviceConfigType<TVariant, TStyles, TVariables>;
}

export interface WidgetDeviceConfigType<
  TVariant extends string,
  TStyles extends object,
  TVariables extends object,
> {
  variant?: TVariant;
  variables?: RecursivePartial<TVariables>;
  styles?: RecursivePartial<TStyles>;
}

export interface ResolvedWidgetDeviceConfigType<
  TVariant extends string,
  TStyles extends object,
  TVariables extends object,
> {
  device: WidgetDeviceType;
  variant: TVariant;
  variables: TVariables;
  styles: TStyles;
}

export const WidgetSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  enabledSitewide: z.boolean().optional().default(false),
  parentId: z.uuid().nullish(),
  widgetType: z.enum(WidgetTypeList),
  config: WidgetConfigSchema,
});

export type WidgetRenderType = "all" | "desktop" | "mobile";

export interface WidgetManagerLikeObject {
  loadQuantityWidget: () => boolean;
  loadDiscountProgressBarWidget: () => boolean;
  loadShippingProgressBarWidget: () => boolean;
  loadMessageBoxWidget: () => boolean;
  updateAllWidgets: () => Promise<void>;
}

export type FontWeight = "lighter" | "normal" | "bold";
export type TextTransform = "lowercase" | "capitalize" | "uppercase" | "none";
export interface Box {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const FontStyleList = [
  "light",
  "light_italic",
  "regular",
  "regular_italic",
  "medium",
  "medium_italic",
  "bold",
  "bold_italic",
] as const;

export type FontStyle = (typeof FontStyleList)[number];

export const FontStyleOptions = [
  { value: "light", label: "Light" },
  { value: "light_italic", label: "Light italic" },
  { value: "regular", label: "Regular" },
  { value: "regular_italic", label: "Italic" },
  { value: "medium", label: "Medium" },
  { value: "medium_italic", label: "Medium italic" },
  { value: "bold", label: "Bold" },
  { value: "bold_italic", label: "Bold italic" },
] as const;

export type WidgetOffer = OfferDto | OfferLikeObject;
export interface CartInfo {
  quantity: number;
  preDiscountAmount: number;
  postDiscountAmount: number;
}
export type TextType = "normal" | "rich";

export const WidgetRenderLocationList = [
  "viewOnly",
  "settings",
  "drawer",
] as const;

export type WidgetRenderLocation = (typeof WidgetRenderLocationList)[number];
